import React from 'react';
import { LuArrowRight } from 'react-icons/lu';
import useInView from '../hooks/useInView';

/* --- Inline mini-illustrations (CSS + SVG only, no assets) --- */

const VisualGhl = () => (
  <div className="hf-viz hf-viz-ghl">
    <div className="hf-ghl-row hf-ghl-row--old">
      <span className="hf-ghl-dot hf-ghl-dot--red" />
      <span className="hf-ghl-label">Twilio · $0.0075</span>
    </div>
    <div className="hf-ghl-arrow">↓</div>
    <div className="hf-ghl-row hf-ghl-row--new">
      <span className="hf-ghl-dot hf-ghl-dot--purple" />
      <span className="hf-ghl-label hf-ghl-label--bold">TextLink · $0.001</span>
      <span className="hf-ghl-chip">95% cheaper</span>
    </div>
  </div>
);

const VisualA2p = () => (
  <div className="hf-viz hf-viz-a2p">
    <div className="hf-form hf-form--rejected">
      <div className="hf-form-top">
        <span>10DLC registration</span>
        <span className="hf-form-status hf-form-status--bad">Rejected</span>
      </div>
      <div className="hf-form-line" />
      <div className="hf-form-line hf-form-line--short" />
    </div>
    <div className="hf-form hf-form--approved">
      <div className="hf-form-top">
        <span>Your own SIM</span>
        <span className="hf-form-status hf-form-status--good">Ready</span>
      </div>
      <div className="hf-form-line" />
      <div className="hf-form-line hf-form-line--short" />
    </div>
  </div>
);

const VisualRates = () => (
  <div className="hf-viz hf-viz-rates">
    <div className="hf-rate-row hf-rate-row--old">
      <span>Twilio</span>
      <span className="hf-rate-num"><s>$0.0075</s></span>
    </div>
    <div className="hf-rate-row hf-rate-row--old">
      <span>Aggregators</span>
      <span className="hf-rate-num"><s>$0.010+</s></span>
    </div>
    <div className="hf-rate-row hf-rate-row--new">
      <span>TextLink</span>
      <span className="hf-rate-num hf-rate-num--new">$0.001</span>
    </div>
    <div className="hf-rate-tag">95% cheaper · per-SMS</div>
  </div>
);

const VisualApi = () => (
  <div className="hf-viz hf-viz-api">
    <pre className="hf-code">{`POST /api/send-sms
{
  "to": "+15551234",
  "text": "Hi from TextLink"
}`}</pre>
  </div>
);

const VisualWarmup_UNUSED = () => (
  <div className="hf-viz hf-viz-warmup">
    <svg viewBox="0 0 200 80" preserveAspectRatio="none" className="hf-warmup-svg">
      <defs>
        <linearGradient id="hfw" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#a855f7" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M0,70 C30,68 50,64 80,52 C110,40 140,28 200,10 L200,80 L0,80 Z" fill="url(#hfw)" />
      <path d="M0,70 C30,68 50,64 80,52 C110,40 140,28 200,10" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" />
    </svg>
    <div className="hf-warmup-labels">
      <span>Day 1</span><span>Day 14</span><span>Day 30</span>
    </div>
  </div>
);

const FEATURES = [
  {
    title: 'Swap Twilio for TextLink inside GoHighLevel',
    body: 'Native GHL integration plugs into conversations, campaigns, and workflows. Your team keeps working the way they already do — at a fraction of the cost.',
    cta: 'GoHighLevel setup',
    href: '/ghl',
    Visual: VisualGhl,
    highlight: true
  },
  {
    title: 'Skip A2P registration',
    body: 'Your SIM is already carrier-registered. No 10DLC forms, no brand verification.',
    cta: 'How it works',
    href: '/how-textlink-works',
    Visual: VisualA2p
  },
  {
    title: 'Carrier rates, not aggregator rates',
    body: '$0.001/SMS through your own SIM vs $0.0075+ through Twilio and the other middlemen. Up to 95% less, forever.',
    cta: 'See pricing',
    href: '/pricing',
    Visual: VisualRates
  },
  {
    title: 'Clean REST API',
    body: 'Four lines to send. Webhooks for every status change — sent, delivered, received.',
    cta: 'Read the docs',
    href: 'https://docs.textlinksms.com',
    external: true,
    Visual: VisualApi
  }
];

export default function HomeFeatures() {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className={`hf${inView ? ' in-view' : ''}`}>
      <div className="hf-container">
        <header className="hf-head">
          <h2 className="hf-title">A new kind of SMS gateway.</h2>
          <p className="hf-sub">Everything you'd pay extra for with a provider, baked in at the SIM level.</p>
        </header>

        <div className="hf-grid">
          {FEATURES.map((f, i) => {
            const { Visual } = f;
            return (
              <a
                key={i}
                href={f.href}
                target={f.external ? '_blank' : undefined}
                rel={f.external ? 'noopener noreferrer' : undefined}
                className={`hf-card${f.highlight ? ' hf-card--highlight' : ''}`}
                style={{ '--i': i }}
              >
                <div className="hf-card-top">
                  <h3 className="hf-card-title">{f.title}</h3>
                  <p className="hf-card-body">{f.body}</p>
                  <span className="hf-card-cta">
                    {f.cta} <LuArrowRight size={14} strokeWidth={2} />
                  </span>
                </div>
                <Visual />
              </a>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        .hf {
          background: #fafafa;
          padding: clamp(6rem, 14vh, 11rem) clamp(1rem, 3vw, 2rem);
          font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #000;
          -webkit-font-smoothing: antialiased;
          border-top: 1px solid rgba(0,0,0,0.05);
          border-bottom: 1px solid rgba(0,0,0,0.05);
        }
        .hf-container { max-width: 1200px; margin: 0 auto; }

        .hf-head { margin-bottom: clamp(3.5rem, 7vh, 5rem); max-width: 680px; }
        .hf-title {
          font-size: clamp(2.25rem, 5.5vw, 3.75rem);
          font-weight: 600;
          letter-spacing: -0.045em;
          line-height: 1.02;
          margin: 0 0 1rem;
          text-align: left;
        }
        .hf-sub {
          font-size: clamp(0.9375rem, 1.4vw, 1.0625rem);
          color: rgba(0,0,0,0.58);
          margin: 0;
          line-height: 1.55;
        }

        .hf-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: clamp(0.875rem, 1.25vw, 1.125rem);
        }
        @media (max-width: 900px) { .hf-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 560px) { .hf-grid { grid-template-columns: 1fr; } }

        /* scroll reveal */
        .hf-head, .hf-card {
          opacity: 0;
          translate: 0 18px;
          transition:
            border-color 220ms ease,
            transform 220ms ease,
            box-shadow 220ms ease,
            opacity 650ms cubic-bezier(0.16, 1, 0.3, 1),
            translate 650ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .hf.in-view .hf-head { opacity: 1; translate: 0 0; }
        .hf.in-view .hf-card {
          opacity: 1;
          translate: 0 0;
          transition-delay: calc(150ms + var(--i, 0) * 70ms);
        }

        .hf-card {
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.07);
          border-radius: 18px;
          padding: clamp(1.5rem, 2vw, 2rem);
          text-decoration: none;
          color: inherit;
          display: flex;
          flex-direction: column;
          gap: 1.75rem;
          overflow: hidden;
          min-height: 340px;
        }
        .hf-card:hover {
          border-color: rgba(0,0,0,0.16);
          transform: translateY(-3px);
          box-shadow: 0 14px 28px -14px rgba(0,0,0,0.08);
        }

        .hf-card--highlight {
          grid-column: span 2;
          background: linear-gradient(135deg, #f5f3ff 0%, #ffffff 100%);
          border-color: rgba(124, 58, 237, 0.22);
        }
        .hf-card--highlight:hover {
          border-color: rgba(124, 58, 237, 0.5);
          box-shadow: 0 18px 36px -14px rgba(124, 58, 237, 0.22);
        }
        @media (max-width: 900px) { .hf-card--highlight { grid-column: span 2; } }
        @media (max-width: 560px) { .hf-card--highlight { grid-column: span 1; } }

        .hf-card-top { display: flex; flex-direction: column; flex: 1; gap: 0.625rem; }
        .hf-card-title {
          font-size: 1.125rem;
          font-weight: 600;
          letter-spacing: -0.025em;
          margin: 0;
          line-height: 1.25;
        }
        .hf-card--highlight .hf-card-title {
          font-size: clamp(1.375rem, 2.2vw, 1.75rem);
          letter-spacing: -0.03em;
          line-height: 1.15;
        }
        .hf-card-body {
          font-size: 0.9375rem;
          color: rgba(0,0,0,0.58);
          line-height: 1.55;
          margin: 0;
        }
        .hf-card--highlight .hf-card-body {
          font-size: 1rem;
          max-width: 54ch;
        }
        .hf-card-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.8125rem;
          font-weight: 500;
          color: #6d28d9;
          margin-top: 0.375rem;
          letter-spacing: -0.005em;
          transition: gap 200ms ease;
        }
        .hf-card:hover .hf-card-cta { gap: 0.55rem; }

        /* === Mini illustrations === */
        .hf-viz {
          margin-top: auto;
          background: #f7f5ff;
          border: 1px solid rgba(124, 58, 237, 0.1);
          border-radius: 12px;
          padding: 1.125rem;
          position: relative;
          overflow: hidden;
        }
        .hf-card--highlight .hf-viz { background: #ffffff; }

        /* GHL */
        .hf-viz-ghl { display: flex; flex-direction: column; align-items: stretch; gap: 0.4rem; }
        .hf-ghl-row {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          background: #fff;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 10px;
          padding: 0.625rem 0.875rem;
          font-size: 0.875rem;
        }
        .hf-ghl-row--old { opacity: 0.55; }
        .hf-ghl-row--new { border-color: rgba(124,58,237,0.35); background: #faf7ff; }
        .hf-ghl-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .hf-ghl-dot--red { background: #ef4444; }
        .hf-ghl-dot--purple { background: #7c3aed; }
        .hf-ghl-label { color: #000; font-weight: 500; }
        .hf-ghl-label--bold { font-weight: 600; }
        .hf-ghl-arrow {
          align-self: center;
          color: rgba(124,58,237,0.5);
          font-weight: 700;
          font-size: 0.875rem;
          line-height: 1;
        }
        .hf-ghl-chip {
          margin-left: auto;
          background: #ede9fe;
          color: #6d28d9;
          font-size: 0.6875rem;
          font-weight: 600;
          padding: 0.15rem 0.5rem;
          border-radius: 999px;
        }

        /* A2P */
        .hf-viz-a2p { display: flex; flex-direction: column; gap: 0.5rem; }
        .hf-form { background: #fff; border: 1px solid rgba(0,0,0,0.08); border-radius: 10px; padding: 0.625rem 0.875rem; font-size: 0.8125rem; }
        .hf-form-top { display: flex; justify-content: space-between; align-items: center; font-weight: 500; margin-bottom: 0.5rem; }
        .hf-form-line { height: 4px; background: rgba(0,0,0,0.06); border-radius: 2px; margin-bottom: 0.3rem; }
        .hf-form-line--short { width: 60%; }
        .hf-form-status { font-size: 0.6875rem; font-weight: 600; padding: 0.1rem 0.5rem; border-radius: 999px; }
        .hf-form-status--bad { background: #fef2f2; color: #b91c1c; }
        .hf-form-status--good { background: #ecfdf5; color: #047857; }
        .hf-form--rejected { opacity: 0.55; }

        /* Carrier rates */
        .hf-viz-rates { display: flex; flex-direction: column; gap: 0.45rem; }
        .hf-rate-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.55rem 0.875rem;
          background: #fff;
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .hf-rate-row--old { color: rgba(0,0,0,0.55); }
        .hf-rate-row--old .hf-rate-num s { color: rgba(0,0,0,0.38); }
        .hf-rate-row--new {
          border-color: rgba(124,58,237,0.35);
          background: linear-gradient(135deg, #faf7ff, #fff);
          box-shadow: 0 6px 18px -10px rgba(124,58,237,0.45);
        }
        .hf-rate-num--new {
          background: linear-gradient(135deg, #7c3aed, #a855f7);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 700;
        }
        .hf-rate-tag {
          font-size: 0.6875rem;
          font-weight: 600;
          color: #6d28d9;
          text-align: right;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        /* API */
        .hf-viz-api { padding: 0; }
        .hf-code {
          margin: 0;
          padding: 1rem;
          background: #0a0a0a;
          color: #e4e4e7;
          font-family: ui-monospace, 'SF Mono', Menlo, monospace;
          font-size: 0.75rem;
          line-height: 1.55;
          border-radius: 11px;
          white-space: pre;
          overflow: auto;
        }

        /* Warmup */
        .hf-viz-warmup { padding-bottom: 0.75rem; }
        .hf-warmup-svg { width: 100%; height: 80px; display: block; }
        .hf-warmup-labels {
          display: flex;
          justify-content: space-between;
          font-size: 0.6875rem;
          color: rgba(0,0,0,0.5);
          margin-top: 0.25rem;
        }
      `}</style>
    </section>
  );
}
